import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, lastValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { LoggerService } from '../services/logger.service';
import { ConfigService } from '../services/config.service';
import { UsageAnalyticsService } from '../services/usage-analytics.service';
import { Result, ListResponse } from '@shared/models';
import * as Entities from '@shared/entities';

@Injectable({
  providedIn: 'root',
})
export class ProgramsApi {
  private serviceUri = '';

  constructor(
    private http: HttpClient,
    private log: LoggerService,
    private config: ConfigService,
    private usageAnalytics: UsageAnalyticsService
  ) {
    this.config.Config.subscribe((config) => {
      if (config?.apiUri) {
        this.serviceUri = `${config.apiUri}/programs/`;
      }
    });
  }

  async loadPrograms(_force: boolean = false): Promise<ListResponse<Entities.Program>> {
    const result = await lastValueFrom(this.http.get<Result<ListResponse<Entities.Program>>>(this.serviceUri));
    if (result.successful) {
      return result.data;
    }

    this.log.displayError(result.message || 'Failed to load programs', 'Programs');
    return { records: [] } as ListResponse<Entities.Program>;
  }

  async create(program: Entities.Program): Promise<Result<number>> {
    const result = await lastValueFrom(this.http.post<Result<number>>(this.serviceUri, program));
    if (result.successful) {
      this.log.displaySuccess('Program created', 'Programs');
      this.usageAnalytics.trackAction('ProgramCreated', {
        props: {
          ProgramId: String(result.data ?? ''),
        },
      });
    } else {
      this.log.displayError(result.message || 'Failed to create program', 'Programs');
    }
    return result;
  }

  async update(id: number, program: Entities.Program): Promise<Result<number>> {
    const result = await lastValueFrom(this.http.put<Result<number>>(this.serviceUri + id, program));
    if (result.successful) {
      this.log.displaySuccess('Program updated', 'Programs');
      this.usageAnalytics.trackAction('ProgramUpdated', {
        props: {
          ProgramId: String(id),
        },
      });
    } else {
      this.log.displayError(result.message || 'Failed to update program', 'Programs');
    }
    return result;
  }

  async delete(id: number): Promise<Result<number>> {
    const result = await lastValueFrom(this.http.delete<Result<number>>(this.serviceUri + id));
    if (result.successful) {
      this.usageAnalytics.trackAction('ProgramDeleted', {
        props: {
          ProgramId: String(id),
        },
      });
    } else {
      console.debug('delete', result);
    }
    return result;
  }

  async listProviderServiceKeys(programId: number): Promise<Result<ListResponse<Entities.ProviderServiceKey>>> {
    return await lastValueFrom(
      this.http.get<Result<ListResponse<Entities.ProviderServiceKey>>>(
        this.serviceUri + programId + '/providerServiceKeys'
      )
    );
  }

  async createProviderServiceKey(programId: number, name: string): Promise<Result<number>> {
    const result = await lastValueFrom(
      this.http.post<Result<number>>(this.serviceUri + programId + '/providerServiceKeys', { name })
    );

    if (result.successful) {
      this.log.displaySuccess('Provider service key created', 'Programs');
    } else {
      this.log.displayError(result.message || 'Failed to create provider service key', 'Programs');
    }

    return result;
  }

  async updateProviderServiceKey(
    programId: number,
    providerServiceKeyId: number,
    name: string
  ): Promise<Result<number>> {
    const result = await lastValueFrom(
      this.http.put<Result<number>>(this.serviceUri + programId + '/providerServiceKeys/' + providerServiceKeyId, {
        name,
      })
    );

    if (result.successful) {
      this.log.displaySuccess('Provider service key updated', 'Programs');
    } else {
      this.log.displayError(result.message || 'Failed to update provider service key', 'Programs');
    }

    return result;
  }

  async deleteProviderServiceKey(programId: number, providerServiceKeyId: number): Promise<Result<void>> {
    const result = await lastValueFrom(
      this.http.delete<Result<void>>(this.serviceUri + programId + '/providerServiceKeys/' + providerServiceKeyId)
    );

    if (result.successful) {
      this.log.displaySuccess('Provider service key deleted', 'Programs');
    } else {
      this.log.displayError(result.message || 'Failed to delete provider service key', 'Programs');
    }

    return result;
  }

  getAll(): Observable<Entities.Program[]> {
    return this.http.get<Result<ListResponse<Entities.Program>>>(this.serviceUri).pipe(
      map((response) => response.data.records),
      catchError(this.handleError<Entities.Program[]>('getAll', []))
    );
  }

  private handleError<T>(_operation = 'operation', result?: T): (error: unknown) => Observable<T> {
    return (error: unknown): Observable<T> => {
      this.log.logError(error);
      return of(result as T);
    };
  }
}
